import React from 'react';

import { DomainIcon, Icons, type DomainKey } from '@shared/components/ui';

import { NAV_ICON_SIZE, NAV_ICON_SLOT } from './navConfig';

interface SideNavRowContentProps {
  isCollapsed: boolean;
  icon: keyof typeof Icons;
  label: string;
  /** When set, render the duotone domain-colored icon instead of the monochrome glyph. */
  domain?: DomainKey;
  /** Expanded-only, non-interactive trailing content (e.g. a count). */
  trailing?: React.ReactNode;
  /** Overlay badge on the icon when collapsed (e.g. unread count). */
  collapsedBadge?: React.ReactNode;
}

/**
 * Inner content of a nav row: a fixed-width icon slot, a toggleable label, and an
 * expanded-only trailing slot. Carries NO rail, height, hover, or text-state — those
 * live on the row's own element via the shell classes. Rendered as the children of a
 * NavLink, a button, or the main button inside a section-header div.
 *
 * The icon slot is LEFT-ANCHORED in both states (never re-centered when collapsed):
 * collapsing the sidebar must not shift the icon horizontally. The slot's left edge is
 * the same x expanded and collapsed; only the label/trailing toggle. (A prior version
 * added `justify-center` on collapse, which centered the wider-than-content slot and
 * pulled icons ~3-4px left — the shift this component now prevents by construction.)
 */
const SideNavRowContent: React.FC<SideNavRowContentProps> = ({
  isCollapsed,
  icon,
  label,
  domain,
  trailing,
  collapsedBadge,
}) => {
  const IconComponent = Icons[icon];
  return (
    <span className="flex w-full items-center whitespace-nowrap">
      <span className={`relative inline-flex ${NAV_ICON_SLOT} flex-shrink-0 justify-center`}>
        {domain ? (
          <DomainIcon domain={domain} size={NAV_ICON_SIZE} className="flex-shrink-0" />
        ) : (
          <IconComponent size={NAV_ICON_SIZE} color="inherit" className="flex-shrink-0" />
        )}
        {collapsedBadge}
      </span>
      {!isCollapsed && <span className="ml-2 min-w-0 truncate">{label}</span>}
      {!isCollapsed && trailing != null && <span className="ml-auto flex-shrink-0">{trailing}</span>}
    </span>
  );
};

export default SideNavRowContent;
